import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyAdminSession } from '../services/api';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../hooks/useToast';

const API_URL = import.meta.env.VITE_API_URL || '/cv/api';

function getAdminToken() {
  return sessionStorage.getItem('adminSessionToken');
}

function authHeaders(extra = {}) {
  return { 'Authorization': `Bearer ${getAdminToken()}`, ...extra };
}

export default function ManageFiles() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingFile, setEditingFile] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    icon: '',
    description: ''
  });
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null });

  // Vérifier l'authentification via JWT session (V2)
  useEffect(() => {
    verifyAdminSession().then(valid => {
      if (!valid) navigate('/admin/login');
    });
  }, [navigate]);

  // Charger les fichiers et métadonnées
  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      
      // Charger la liste des fichiers
      const filesResponse = await fetch(`${API_URL}/files/list`);
      const filesData = await filesResponse.json();
      
      // Charger les métadonnées personnalisées
      const metadataResponse = await fetch(`${API_URL}/files/metadata`);
      const metadataData = await metadataResponse.json();
      
      setFiles(filesData.files || []);
      setMetadata(metadataData.metadata || {});
    } catch (error) {
      console.error('Erreur chargement fichiers:', error);
      showToast('Erreur lors du chargement des fichiers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (file) => {
    setEditingFile(file.filename);
    
    // Utiliser les métadonnées personnalisées si elles existent
    const customMetadata = metadata[file.filename];
    
    setFormData({
      label: customMetadata?.label || file.label,
      icon: customMetadata?.icon || file.icon,
      description: customMetadata?.description || file.description
    });
  };

  const handleSaveMetadata = async () => {
    try {
      const response = await fetch(`${API_URL}/files/metadata`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          filename: editingFile,
          ...formData
        })
      });

      if (response.ok) {
        showToast('Métadonnées sauvegardées !');
        setEditingFile(null);
        loadFiles();
      } else {
        showToast('Erreur lors de la sauvegarde', 'error');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showToast('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = (filename) => {
    if (filename === 'cv.pdf') {
      showToast('Le fichier cv.pdf ne peut pas être supprimé (fichier par défaut)', 'warning');
      return;
    }
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer le fichier',
      message: `Voulez-vous vraiment supprimer ${filename} ? Cette action est irréversible.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/files/${filename}`, {
            method: 'DELETE',
            headers: authHeaders(),
          });
          if (response.ok) {
            showToast('Fichier supprimé !');
            loadFiles();
          } else {
            const data = await response.json();
            showToast(data.error || 'Erreur lors de la suppression', 'error');
          }
        } catch (error) {
          console.error('Erreur suppression:', error);
          showToast('Erreur lors de la suppression', 'error');
        }
        setConfirmDialog({ isOpen: false, onConfirm: null });
      }
    });
  };

  const uploadFile = async (file) => {
    if (!file) return;

    // Vérifier que c'est un PDF
    if (file.type !== 'application/pdf') {
      showToast('Seuls les fichiers PDF sont acceptés', 'warning');
      return;
    }

    // Vérifier la taille (max 10 MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast('Le fichier est trop volumineux (max 10 MB)', 'warning');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/files/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAdminToken()}` },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        showToast(data.message);
        setShowUploadForm(false);
        loadFiles();
      } else {
        showToast(data.error || 'Erreur lors de l\'upload', 'error');
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      showToast('Erreur lors de l\'upload du fichier', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    await uploadFile(file);
    // Reset input
    event.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await uploadFile(e.dataTransfer.files[0]);
    }
  };

  const getFileMetadata = (file) => {
    return metadata[file.filename] || {
      label: file.label,
      icon: file.icon,
      description: file.description
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des fichiers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Gestionnaire de documents
            </h1>
            <p className="text-gray-600">
              Uploadez et gérez vos fichiers PDF (CV, certificats, diplômes...)
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowUploadForm(!showUploadForm)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Ajouter un document</span>
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour Admin</span>
            </button>
          </div>
        </div>

        {/* Formulaire d'upload */}
        {showUploadForm && (
          <div className="mb-8 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Uploader un nouveau document
            </h2>
            <div className="space-y-4">
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? 'border-green-500 bg-green-50' : 'border-gray-300'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleUpload}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className={`cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    {uploading ? 'Upload en cours...' : 'Cliquez ou glissez un fichier PDF ici'}
                  </p>
                  <p className="text-sm text-gray-500">
                    PDF uniquement — Taille max : 10 MB — Accents et Unicode supportés
                  </p>
                </label>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Conseil :</strong> Les noms de fichiers avec accents (ex: certificat-généraliste.pdf) sont supportés.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Fichiers PDF</p>
                <p className="text-2xl font-bold text-gray-900">{files.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Personnalisés</p>
                <p className="text-2xl font-bold text-gray-900">{Object.keys(metadata).length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Taille totale</p>
                <p className="text-2xl font-bold text-gray-900">
                  {files.reduce((sum, f) => sum + f.size, 0) > 1024 * 1024 
                    ? `${(files.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024)).toFixed(1)} MB`
                    : `${(files.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(0)} KB`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des fichiers */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            📋 Fichiers disponibles
          </h2>

          {files.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600">Aucun fichier PDF trouvé dans /assets</p>
            </div>
          ) : (
            <div className="space-y-4">
              {files.map((file) => {
                const meta = getFileMetadata(file);
                const isEditing = editingFile === file.filename;

                return (
                  <div
                    key={file.filename}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {isEditing ? (
                      // Mode édition
                      <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900 mb-4">
                          ✏️ Éditer les métadonnées de : {file.filename}
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Label
                            </label>
                            <input
                              type="text"
                              value={formData.label}
                              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                              placeholder="CV Senior"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Icône (emoji)
                            </label>
                            <input
                              type="text"
                              value={formData.icon}
                              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                              placeholder="🎯"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description
                            </label>
                            <input
                              type="text"
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                              placeholder="5+ ans d'expérience"
                            />
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handleSaveMetadata}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            ✅ Sauvegarder
                          </button>
                          <button
                            onClick={() => setEditingFile(null)}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                          >
                            ❌ Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Mode affichage
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <span className="text-4xl">{meta.icon}</span>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {meta.label}
                            </h3>
                            <p className="text-sm text-gray-600">{meta.description}</p>
                            <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                              <span>📄 {file.filename}</span>
                              <span>💾 {file.sizeFormatted}</span>
                              <span>🕐 {new Date(file.lastModified).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(file)}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          >
                            ✏️ Éditer
                          </button>
                          {file.filename !== 'cv.pdf' && (
                            <button
                              onClick={() => handleDelete(file.filename)}
                              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                            >
                              🗑️ Supprimer
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            💡 Comment ça marche ?
          </h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>1. Les fichiers PDF sont lus depuis le dossier <code className="bg-blue-100 px-2 py-1 rounded">/app/assets</code></li>
            <li>2. Cliquez sur "✏️ Éditer" pour personnaliser le label, l'icône et la description</li>
            <li>3. Les métadonnées sont sauvegardées dans <code className="bg-blue-100 px-2 py-1 rounded">metadata.json</code></li>
            <li>4. Ces informations seront utilisées dans le formulaire de génération de liens</li>
            <li>5. Les fichiers PDF ne sont jamais accessibles publiquement — uniquement via lien sécurisé (token JWT)</li>
          </ul>
        </div>
      </div>
      <Toast toast={toast} onClose={hideToast} />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, onConfirm: null })}
      />
    </div>
  );
}
